import * as React from 'react';

import {Button} from 'shells/button/button';
import {SlideshowControls} from 'shells/carousel/slideshow-controls';
import {ErrorMessage} from 'shells/form/error-message';
import {Link} from 'shells/link/link';
import {FooterWithControls, RightFrame, ModalMain} from 'shells/modal/step-modal/layout';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {ModalBody} from 'shells/typography/modal-body';
import {ModalHeader} from 'shells/typography/modal-header';

import type {SmtpPortType} from 'nutshell-graphql-types';
import type {UserEmail} from 'nutshell-graphql-types';

import {SMTP_DEFAULTS} from '../../smtp/get-settings-from-user-email';
import {SmtpFormAuthenticationFields} from '../../smtp/smtp-form-authentication-fields';
import {SmtpFormServerFields} from '../../smtp/smtp-form-server-fields';
import {SmtpSubmitButton} from '../../smtp/smtp-submit-button';

import {trackModalPageLoad} from '../track-page-load';

import {UserEmailAddressSelection} from './user-email-address-selection';

const DEFAULT_PORT = SMTP_DEFAULTS.port;
const DEFAULT_PORT_TYPE = SMTP_DEFAULTS.ssl;

type Props = {
    currentUrl: string;
    goBack: () => void;
    onSuccess: () => void;
    onVerificationFailure: (arg1: UserEmail) => void;
    onVerificationTimeout: () => void;
};

export function ConfigureSmtp(props: Props) {
    const submitButton = React.useRef<HTMLButtonElement | null | undefined>(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
    const [slideNum, setSlideNum] = React.useState<number>(0);
    const [selectedUserEmailId, setSelectedUserEmailId] = React.useState<string>('');

    const [hostname, setHostname] = React.useState<string>('');
    const [port, setPort] = React.useState<string>(DEFAULT_PORT);
    const [portType, setPortType] = React.useState<SmtpPortType>(DEFAULT_PORT_TYPE);
    const [username, setUsername] = React.useState<string>('');
    const [password, setPassword] = React.useState<string>('');

    React.useEffect(() => {
        trackModalPageLoad(props.currentUrl);
    }, [props.currentUrl]);

    function advanceSlide() {
        setSlideNum(slideNum + 1);
    }

    return (
        <RightFrame>
            <ModalMain>
                {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : undefined}
                {slideNum === 0 ? (
                    <React.Fragment>
                        <SpacerGroup mb={32}>
                            <ModalHeader>Choose your email address</ModalHeader>
                            <ModalBody>
                                Which email address would you like to use for sending email through
                                your mail server? (This is usually just the email address you used
                                to sign up for Nutshell!)
                            </ModalBody>
                        </SpacerGroup>
                        <UserEmailAddressSelection
                            selectedAddress={selectedUserEmailId}
                            onSelectAddress={(emailAddress) => {
                                if (emailAddress) {
                                    setHostname(getHostnameFromEmail(emailAddress.address));
                                    setUsername(emailAddress.address);
                                    setSelectedUserEmailId(emailAddress.id);
                                }
                            }}
                        />
                    </React.Fragment>
                ) : (
                    <div />
                )}
                {slideNum === 1 ? (
                    <React.Fragment>
                        <SpacerGroup mb={32}>
                            <ModalHeader>We need to gather some information</ModalHeader>
                            <ModalBody>
                                We’ll need your hostname, port type, and port number in order to
                                connect to your email provider. We’ve made some guesses below based
                                on common settings.
                            </ModalBody>
                        </SpacerGroup>
                        <SmtpFormServerFields
                            hostname={hostname}
                            portType={portType}
                            port={port}
                            onChangeHostname={setHostname}
                            onChangePortType={setPortType}
                            onChangePort={setPort}
                            onEnter={advanceSlide}
                        />
                        <ModalBody>
                            Not sure where to look?{' '}
                            <Link
                                variant='primary'
                                href='https://support.nutshell.com/en/articles/8428903-how-to-send-and-get-emails-from-inside-nutshell'
                            >
                                Click here for help
                            </Link>
                        </ModalBody>
                    </React.Fragment>
                ) : (
                    <div />
                )}
                {slideNum === 2 ? (
                    <React.Fragment>
                        <SpacerGroup mb={32}>
                            <ModalHeader>Enter your credentials to connect</ModalHeader>
                            <ModalBody>
                                Enter your username and password to give Nutshell permission for you
                                to send emails.
                            </ModalBody>
                        </SpacerGroup>
                        <SmtpFormAuthenticationFields
                            username={username}
                            password={password}
                            onChangeUsername={setUsername}
                            onChangePassword={setPassword}
                            onEnter={() => {
                                if (submitButton.current) {
                                    submitButton.current.click();
                                }
                            }}
                        />
                        <ModalBody>
                            Not sure where to look?{' '}
                            <Link
                                variant='primary'
                                href='https://support.nutshell.com/en/articles/8428903-how-to-send-and-get-emails-from-inside-nutshell'
                            >
                                Click here for help
                            </Link>
                        </ModalBody>
                    </React.Fragment>
                ) : (
                    <div />
                )}
            </ModalMain>
            <FooterWithControls
                controls={<SlideshowControls selectedSlide={slideNum} slideCount={3} />}
                buttons={
                    <React.Fragment>
                        <Button
                            variant='text'
                            onClick={() =>
                                slideNum === 0 ? props.goBack() : setSlideNum(slideNum - 1)
                            }
                        >
                            Back
                        </Button>
                        {slideNum === 2 ? (
                            <SmtpSubmitButton
                                // @ts-expect-error
                                ref={submitButton}
                                onVerificationFailure={props.onVerificationFailure}
                                onVerificationTimeout={props.onVerificationTimeout}
                                onError={setErrorMessage}
                                onSuccess={props.onSuccess}
                                disabled={
                                    !areInputsValid({
                                        hostname,
                                        port,
                                        portType,
                                        username,
                                        password,
                                    })
                                }
                                smtpSettings={{
                                    hostname,
                                    port,
                                    ssl: portType,
                                    username,
                                    password,
                                    userEmailId: selectedUserEmailId,
                                }}
                            />
                        ) : (
                            <Button
                                variant='primary'
                                disabled={!selectedUserEmailId}
                                size='big'
                                onClick={advanceSlide}
                            >
                                Next
                            </Button>
                        )}
                    </React.Fragment>
                }
            />
        </RightFrame>
    );
}

// @ts-expect-error
function areInputsValid({hostname, port, portType, username, password}) {
    if (hostname.trim().length === 0) {
        return false;
    }

    if (port.trim().length === 0) {
        return false;
    }

    if (portType.trim().length === 0) {
        return false;
    }

    if (username.trim().length === 0) {
        return false;
    }

    if (password.trim().length === 0) {
        return false;
    }

    return true;
}

function getHostnameFromEmail(emailAddress: string): string {
    return `smtp.${emailAddress.split('@')[1]}`;
}
